/**
 * Sync: consent, passphrase, on/off, status. FR-501..FR-509, L-04, S-06.
 *
 * ## This screen is the legal basis
 *
 * Menstrual data is special-category under GDPR Article 9 and KVKK Article 6, and
 * the workable basis is explicit consent — specific, informed, unambiguous, freely
 * given, separately obtained, not bundled. That is a description of a *screen*, and
 * this is it. If a user could not tell from what is rendered here what leaves the
 * device, who receives it and what cannot be undone, the consent is not informed
 * and the processing has no basis.
 *
 * So the consent step is its own view, it lists the actual relays with their
 * jurisdictions, and it says the uncomfortable parts — a relay sees your IP and
 * when you sync; copies that reach a relay may never be deleted — rather than only
 * the reassuring ones.
 *
 * ## Lives in the data feature on purpose
 *
 * Sync belongs to "your data", and putting it here avoids a cross-feature import
 * that `no-feature-cycles` would refuse (architecture Section 4).
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { t } from '@copy/index'
import {
  clearSyncIdentity,
  consentAllowsSync,
  consentStatus,
  deriveSyncIdentity,
  grantSyncConsent,
  loadRelays,
  loadSyncIdentity,
  readSyncConsent,
  saveSyncIdentity,
  secretStrength,
  withdrawSyncConsent,
  type ConsentStatus,
  type RelayEntry,
} from '@shared/data/private'
import { Notice } from '@shared/ui'

type View = 'summary' | 'consent'

export function SyncSection() {
  const [relays, setRelays] = useState<RelayEntry[]>([])
  const [listRejected, setListRejected] = useState(false)
  const [status, setStatus] = useState<ConsentStatus>({ state: 'none' })
  const [syncAlias, setSyncAlias] = useState<string | undefined>(undefined)
  const [view, setView] = useState<View>('summary')
  const sectionRef = useRef<HTMLElement>(null)
  const isInitial = useRef(true)

  const refresh = useCallback(async () => {
    const list = await loadRelays()
    setRelays(list.relays)
    setListRejected(list.rejected)
    setStatus(
      consentStatus(
        await readSyncConsent(),
        list.relays.map((relay) => hostOf(relay.url)),
      ),
    )
    const identity = await loadSyncIdentity()
    setSyncAlias(identity?.alias)
  }, [])

  useEffect(() => {
    // The rule traces `refresh` to a setState and assumes a synchronous cascade.
    // It is not one: every setState runs after an awaited relay-list fetch and an
    // awaited consent read. Loading persisted state on mount is what an effect is
    // for.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void refresh().catch(() => undefined)
  }, [refresh])

  useEffect(() => {
    if (isInitial.current) {
      isInitial.current = false
      return
    }
    // Scroll the top of the Sync section into view when transitioning between summary and consent
    sectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
  }, [view])

  const enabled = consentAllowsSync(status)

  return (
    <section ref={sectionRef} className="flex flex-col gap-3 border-t border-line pt-6">
      <h2 className="text-sm font-medium">{t('sync.heading')}</h2>

      {listRejected ? <Notice tone="error">{t('sync.status.listRejected')}</Notice> : null}

      {view === 'consent' ? (
        <ConsentView
          relays={relays}
          onCancel={() => setView('summary')}
          onGranted={async () => {
            setView('summary')
            await refresh()
          }}
        />
      ) : (
        <SummaryView
          enabled={enabled}
          status={status}
          syncAlias={syncAlias}
          relays={relays}
          onReview={() => setView('consent')}
          onTurnOff={async () => {
            await withdrawSyncConsent()
            // The identity goes with the consent. Leaving it would let the app
            // rejoin the account without asking for the passphrase again, which
            // is not what "off" means to the person who pressed it.
            await clearSyncIdentity()
            await refresh()
          }}
        />
      )}
    </section>
  )
}

function SummaryView({
  enabled,
  status,
  syncAlias,
  relays,
  onReview,
  onTurnOff,
}: {
  enabled: boolean
  status: ConsentStatus
  syncAlias: string | undefined
  relays: readonly RelayEntry[]
  onReview: () => void
  onTurnOff: () => Promise<void>
}) {
  return (
    <>
      <Notice tone={enabled ? 'info' : 'warning'}>
        {enabled ? t('sync.on') : t('sync.off')}
      </Notice>

      {enabled && syncAlias ? (
        <p className="text-xs text-ink-muted -mt-1 mb-2">
          Sync account fingerprint: <code className="bg-line/30 px-1 py-0.5 rounded font-mono select-all">{syncAlias.slice(0, 12)}...</code>
        </p>
      ) : null}

      <p className="text-xs text-ink-muted mb-2">
        {t('sync.lockHint')}
      </p>

      {/* Each of these pauses sync rather than warning, because continuing to
          process special-category data on a consent that no longer covers it is
          processing without a basis, whatever the screen says. */}
      {status.state === 'relays-changed' ? (
        <Notice tone="warning" action={{ label: t('sync.status.reviewRelays'), onClick: onReview }}>
          {t('sync.status.relaysChanged')}
        </Notice>
      ) : null}

      {status.state === 'outdated' ? (
        <Notice tone="warning" action={{ label: t('sync.status.reviewRelays'), onClick: onReview }}>
          {t('sync.status.consentOutdated')}
        </Notice>
      ) : null}

      {relays.length === 0 ? <Notice tone="warning">{t('sync.status.noRelays')}</Notice> : null}

      {enabled ? (
        <button
          type="button"
          onClick={() => void onTurnOff()}
          className="min-h-11 rounded-lg border border-line text-sm transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
        >
          {t('sync.turnOff')}
        </button>
      ) : (
        <button
          type="button"
          onClick={onReview}
          disabled={relays.length === 0}
          className="min-h-11 rounded-lg border border-line text-sm disabled:opacity-50 transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
        >
          {t('sync.turnOn')}
        </button>
      )}
    </>
  )
}

function ConsentView({
  relays,
  onCancel,
  onGranted,
}: {
  relays: readonly RelayEntry[]
  onCancel: () => void
  onGranted: () => Promise<void>
}) {
  const [mode, setMode] = useState<'register' | 'login'>('register')
  const [username, setUsername] = useState('')
  const [passphrase, setPassphrase] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string>()
  const [busy, setBusy] = useState(false)

  const strength = passphrase === '' ? undefined : secretStrength(passphrase)

  const submit = async () => {
    setError(undefined)
    if (username.trim() === '') {
      setError(t('sync.username.required'))
      return
    }
    if (mode === 'register') {
      if (passphrase !== confirm) {
        setError(t('sync.passphrase.mismatch'))
        return
      }
      // FR-504 / S-06. Refused, not warned: a weak passphrase on records that leave
      // the device is crackable offline by anyone who copies them from a relay, and
      // unlike the app PIN there is no device in the way.
      if (strength === 'weak') {
        setError(t('sync.passphrase.tooWeak'))
        return
      }
    }
    setBusy(true)
    try {
      /*
       * Derive BEFORE recording consent.
       *
       * Deriving costs 600,000 PBKDF2 iterations and can fail — the app can be
       * locked, and storing the identity needs the data key. Recording consent
       * first would leave the app believing sync is on with no identity to sync
       * as, which reads on screen as "enabled and permanently doing nothing".
       *
       * The passphrase itself is never stored. Only what is derived from it.
       */
      await saveSyncIdentity(await deriveSyncIdentity(username, passphrase))
      await grantSyncConsent(relays.map((relay) => hostOf(relay.url)))
    } catch {
      setError(t('sync.setupFailed'))
      return
    } finally {
      setBusy(false)
    }
    await onGranted()
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Tab Selector */}
      <div className="flex border-b border-line mb-2">
        <button
          type="button"
          onClick={() => {
            setMode('register')
            setError(undefined)
            setConfirm('')
          }}
          className={`flex-1 pb-2 text-sm font-medium border-b-2 text-center transition-colors ${
            mode === 'register'
              ? 'border-accent text-ink'
              : 'border-transparent text-ink-muted hover:text-ink'
          }`}
        >
          {t('sync.consent.tabRegister')}
        </button>
        <button
          type="button"
          onClick={() => {
            setMode('login')
            setError(undefined)
            setConfirm('')
          }}
          className={`flex-1 pb-2 text-sm font-medium border-b-2 text-center transition-colors ${
            mode === 'login'
              ? 'border-accent text-ink'
              : 'border-transparent text-ink-muted hover:text-ink'
          }`}
        >
          {t('sync.consent.tabLogin')}
        </button>
      </div>

      <h3 className="text-sm font-medium">
        {mode === 'register' ? t('sync.consent.title') : t('sync.consent.connectTitle')}
      </h3>

      {/* The four things a regulator would ask, in the order a person would. */}
      <p className="text-xs text-ink-muted">{t('sync.consent.whatLeaves')}</p>
      <p className="text-xs text-ink-muted">{t('sync.consent.whatTheySee')}</p>

      <div>
        <p className="text-xs font-medium">{t('sync.consent.whoReceives')}</p>
        <ul className="pt-1">
          {relays.map((relay) => (
            <li key={relay.url} className="py-1 text-xs text-ink-muted">
              {/* Named, with jurisdiction. Consent is to specific recipients, and
                  jurisdiction decides whose courts can compel them. */}
              <span className="block">
                {t('sync.consent.relayLine', {
                  operator: relay.operator,
                  jurisdiction: relay.jurisdiction,
                })}
              </span>
              <span className="block">
                {t('sync.consent.relayContact', { contact: relay.contact })}
              </span>
            </li>
          ))}
        </ul>
        <p className="text-xs text-ink-muted">{t('sync.consent.jurisdictionNote')}</p>
      </div>

      <p className="text-xs text-ink-muted">{t('sync.consent.passphrase')}</p>

      <label htmlFor="sync-username" className="text-sm">
        {mode === 'register' ? t('sync.username.label') : t('sync.username.loginLabel')}
      </label>
      <input
        id="sync-username"
        type="text"
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        value={username}
        onChange={(event) => setUsername(event.target.value)}
        className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
      />
      <p className="text-xs text-ink-muted">
        {mode === 'register' ? t('sync.username.hint') : t('sync.username.loginHint')}
      </p>

      <label htmlFor="sync-passphrase" className="text-sm">
        {mode === 'register' ? t('sync.passphrase.label') : t('sync.passphrase.loginLabel')}
      </label>
      <input
        id="sync-passphrase"
        type="password"
        autoComplete="new-password"
        value={passphrase}
        onChange={(event) => setPassphrase(event.target.value)}
        className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
      />

      {mode === 'register' ? (
        <>
          <label htmlFor="sync-passphrase-confirm" className="text-sm">
            {t('sync.passphrase.confirm')}
          </label>
          <input
            id="sync-passphrase-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            className="min-h-11 rounded-lg border border-line bg-surface-raised px-3 transition-all duration-200 hover:border-ink-muted/50 focus:border-accent focus:outline-none"
          />
        </>
      ) : null}
      
      <p className="text-xs text-ink-muted">{t('sync.passphrase.hint')}</p>

      {/* Said before agreeing, not after. */}
      <Notice tone="warning">{t('sync.consent.noUndo')}</Notice>
      <p className="text-xs text-ink-muted">{t('sync.consent.withdraw')}</p>
      <p className="text-xs text-ink-muted mb-2">{t('sync.lockHint')}</p>

      {error !== undefined ? <Notice tone="error">{error}</Notice> : null}

      <button
        type="button"
        onClick={() => void submit()}
        disabled={busy || username.trim() === '' || passphrase === '' || (mode === 'register' && confirm === '')}
        className="min-h-11 rounded-lg bg-accent text-sm text-accent-ink disabled:opacity-50 transition-all duration-200 hover:opacity-90 active:scale-[0.98]"
      >
        {mode === 'register' ? t('sync.consent.agree') : t('sync.consent.connect')}
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="min-h-11 rounded-lg border border-line text-sm transition-all duration-200 hover:bg-line/40 active:scale-[0.98]"
      >
        {t('sync.consent.cancel')}
      </button>
    </div>
  )
}

/** Hostname only. The consent record compares hosts, not full URLs with paths. */
function hostOf(url: string): string {
  try {
    return new URL(url).hostname.toLowerCase()
  } catch {
    return url
  }
}
